import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { BLOCKING_BEHAVIOR, BLOCKING_CONFIG, type BlockingBehavior } from '../../../constants/fraud-constants';
import Global from '../../../store/global-selectors';
import { useFraudIndicators } from './use-fraud-indicators';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { FraudIndicator } from '../../../types/fraud-detection';

type UseFraudIndicatorsForGridReturn = {
  hasFraudIndicators: boolean;
  mostSevereIcon: IconDefinition | null;
  mostSevereClassName: string;
  indicators: FraudIndicator[];
};

/**
 * Hook for fraud indicators optimized for grid display
 * Returns the most severe indicator icon and sorted list for tooltip
 * @param fulfillmentPlanId - The fulfillment plan ID to fetch indicators for
 * @param isEnabled - Whether the feature is enabled (skips selector + computation when false)
 */
export const useFraudIndicatorsForGrid = (fulfillmentPlanId?: string, isEnabled = true): UseFraudIndicatorsForGridReturn => {
  const fulfillmentPlan = useSelector((state) =>
    fulfillmentPlanId && isEnabled ? Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) : undefined,
  );

  const { indicators, hasAnyIndicators, hasBlockingIndicators, hasWarningIndicators } = useFraudIndicators(fulfillmentPlan);

  const gridData = useMemo(() => {
    if (!hasAnyIndicators) {
      return {
        hasFraudIndicators: false,
        mostSevereIcon: null,
        mostSevereClassName: '',
        indicators: [],
      };
    }

    // Sort indicators by severity (BLOCKS → FLAGS → INFO)
    const sortedIndicators = [...indicators].sort((a, b) => {
      const severityOrder = {
        [BLOCKING_BEHAVIOR.BLOCKS]: 0,
        [BLOCKING_BEHAVIOR.FLAGS]: 1,
        [BLOCKING_BEHAVIOR.INFO]: 2,
      };
      return severityOrder[a.blockingBehavior] - severityOrder[b.blockingBehavior];
    });

    // Determine most severe behavior
    let mostSevereBehavior: BlockingBehavior = BLOCKING_BEHAVIOR.INFO;
    if (hasBlockingIndicators) {
      mostSevereBehavior = BLOCKING_BEHAVIOR.BLOCKS;
    } else if (hasWarningIndicators) {
      mostSevereBehavior = BLOCKING_BEHAVIOR.FLAGS;
    }

    const config = BLOCKING_CONFIG[mostSevereBehavior];

    return {
      hasFraudIndicators: true,
      mostSevereIcon: config.icon,
      mostSevereClassName: config.className,
      indicators: sortedIndicators,
    };
  }, [indicators, hasAnyIndicators, hasBlockingIndicators, hasWarningIndicators]);

  return gridData;
};
